import React, {useEffect} from "react";
import {format} from "date-fns";
import {View, Box, Text} from "@unibuddy/patron";
import {Redirect, useHistory} from "react-router-dom";
import {useMutation} from "react-apollo";
import {useTranslation} from "@unibuddy/intl";

import {setMe} from "shared/Auth/actions/authActions";
import {productSpec, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import Auth from "shared/Auth/auth";
import useAuth from "shared/Auth/hooks/useAuth";
import {rootURL} from "shared/Utils/WhereAmI";

import {getReduxDispatch} from "../../../../App";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";
import {getAgeGateInfo} from "../../Verification/VerifyAge/AgeVerificationApi";
import {AuthPageContainer} from "../components/AuthPageContainer";
import {AgeVerificationForm} from "../components/AgeVerificationForm";
import {InstitutionPolicyTextShort} from "../components/InstitutionPolicyTextShort";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_VERIFY_AGE} from "../../../../translations/verifyAge";
import {updateApplicantAgeVerification} from "../data/updateApplicantAgeVerificationMutation";

export const AgeVerificationPage = () => {
    const {t} = useTranslation(IntlNamespaces.VERIFY_AGE);
    const {slug, features} = useUniversity();
    const history = useHistory();
    const {trackEvent} = useAnalytics();
    const {authState} = useAuth();
    const [updateApplicant] = useMutation(updateApplicantAgeVerification);
    const dispatch = getReduxDispatch();
    const alreadyLoggedIn = Auth.loggedIn(authState);
    const rootUrl = rootURL(location.pathname);
    const ageVerificationR1Enabled = features.get("ageVerificationR1");
    const hasAgeVerificationInfo = authState?.me?.applicant?.ageVerificationInfo ?? null;

    useEffect(() => {
        trackEvent(sharedEvents.ageSignUpPageViewed.name, {
            product: productSpec.embeddedUniversityWidget.NAME,
            universitySlug: slug,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (dob: Date): Promise<void> => {
        try {
            const dateOfBirth = format(dob, "yyyy-MM-dd");
            const response = await getAgeGateInfo(dateOfBirth);
            trackEvent(sharedEvents.continueToSignUpClickedOnAgePage.name, {
                product: productSpec.embeddedUniversityWidget.NAME,
                universitySlug: slug,
                ageSuccess: !response.isMinor,
            });
            const ageVerificationInfo = {
                countryCode: response.country,
                age: response.age,
                consentAgeForCountry: response.consentAgeForCountry,
                isMinor: response.isMinor,
                dateOfBirth,
            };

            dispatch(
                setMe({
                    applicant: {
                        ageVerificationInfo,
                    },
                }),
            );

            if (alreadyLoggedIn) {
                await updateApplicant({
                    variables: {
                        ageVerificationInfo,
                    },
                });

                if (response.isMinor) {
                    return history.replace({
                        pathname: `${rootUrl}/auth/minor`,
                        search: location.search,
                    });
                }
            }
        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    if (ageVerificationR1Enabled && hasAgeVerificationInfo?.isMinor) {
        return <Redirect to={`${rootUrl}/auth/minor`} />;
    }

    if (!ageVerificationR1Enabled || (ageVerificationR1Enabled && hasAgeVerificationInfo)) {
        return (
            <Redirect
                to={{
                    pathname: `${rootUrl}/auth/register2`,
                    state: {nextPathname: history?.location?.state?.nextPathname},
                }}
            />
        );
    }

    return (
        <AuthPageContainer>
            <View gap="medium-large" minW="100%">
                <Box gap="small" paddingY="xsmall">
                    <Text size="large" weight="bold" align="center">
                        {t(INTL_VERIFY_AGE.key, INTL_VERIFY_AGE.defaultValue)}
                    </Text>
                </Box>
                <AgeVerificationForm handleSubmit={handleSubmit} />
                <InstitutionPolicyTextShort />
            </View>
        </AuthPageContainer>
    );
};
